import type {
  ActionConfig,
  SoftphoneActionType,
  SoftphoneCallActionProps,
} from './index.types';
import { ReactComponent as PhoneIcon } from 'Assets/icons/call.svg';
import { ReactComponent as HangUpIcon } from 'Assets/icons/hangup_v2.svg';
import { SoftphoneShortcutTooltip } from 'Modules/softphone/components/SoftphoneShortcutTooltip';
import { getShortcutAriaLabel } from 'Modules/softphone/constants/keyboardShortcuts';
import { TOOLTIP_DELAY_MS } from 'Modules/softphone/constants/timing';
import {
  useCallButtonValidation,
  useSoftphoneCallControls,
} from 'Modules/softphone/hooks';
import { useMultipleCallsManager } from 'Modules/softphone/hooks/useMultipleCallsManager';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { getCallActionCopy } from 'Modules/softphone/utils/callActionCopy';
import React from 'react';
import { IconButton } from '~/lib/ui/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/lib/ui/tooltip';

/**
 * SoftphoneCallAction Component
 * Generic action button that can render different call actions (hangup, call, etc)
 * based on the current call state and dialpad visibility
 */
export const SoftphoneCallAction = ({
  testID,
}: SoftphoneCallActionProps): React.ReactElement => {
  const descriptionId = React.useId();
  const { permissions, handleCall, handleEndCall } = useSoftphoneCallControls();
  const { currentCall } = useMultipleCallsManager();

  const { dialpadDigits, isDialpadActive } = useSoftphoneStore((state) => ({
    dialpadDigits: state.dialpadDigits,
    isDialpadActive: state.isDialpadActive,
  }));

  const actionType: SoftphoneActionType =
    currentCall && !isDialpadActive ? 'hangup' : 'call';

  const callButtonState = useCallButtonValidation({
    dialpadDigits,
    hasCurrentCall: !!currentCall,
  });

  const actionConfigs: Record<string, ActionConfig> = {
    hangup: {
      icon: HangUpIcon,
      label: getCallActionCopy('end').caption,
      variant: 'destructive',
      handler: handleEndCall,
      disabled: !permissions.canHangUp,
    },
    call: {
      icon: PhoneIcon,
      label: 'Call',
      variant: 'success',
      handler: () => handleCall(dialpadDigits),
      disabled: callButtonState.disabled,
      helperText: callButtonState.helperText,
    },
  };

  const { icon, label, handler, variant, disabled, helperText } =
    actionConfigs[actionType];

  const isHangUp = actionType === 'hangup';
  // A disabled button can't run its action — and the End Call shortcut is gated
  // by the same permission — so it must not advertise the chord.
  const showEndShortcut = isHangUp && !disabled;
  const endCopy = getCallActionCopy('end', {
    isDisabled: disabled,
    reason: helperText,
  });
  const tooltipText = isHangUp ? endCopy.tooltip : helperText;

  // Name, state and reason each ride the attribute meant for them, so nothing
  // is announced twice — see SoftphoneActionButton. The chord goes only on an
  // action that can run; the reason reaches assistive tech as a description
  // rather than a tooltip it never opens.
  const accessibleName =
    !disabled && showEndShortcut ? getShortcutAriaLabel(label, 'end') : label;
  const description =
    disabled && tooltipText !== label ? tooltipText : undefined;

  const button = (
    <div className="col-span-3">
      <IconButton
        icon={icon}
        label={label}
        onClick={() => void handler()}
        variant={variant}
        iconClassName="brightness-0 invert"
        disabled={disabled}
        ariaLabel={accessibleName}
        keepFocusableWhenDisabled
        ariaDescribedBy={description ? descriptionId : undefined}
        testID={testID ? `${testID}-${actionType}` : undefined}
      />
      {description && (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      )}
    </div>
  );

  // On an established call the button ends it, so surface the End Call
  // shortcut (Alt+Shift+E) here the same way the other call actions do.
  if (showEndShortcut) {
    return (
      <SoftphoneShortcutTooltip
        action="end"
        label={endCopy.tooltip ?? endCopy.caption}
        sideOffset={-19}
      >
        {button}
      </SoftphoneShortcutTooltip>
    );
  }

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>

      {tooltipText && (
        <TooltipContent side="bottom" sideOffset={-19}>
          <p className='text-[12px] whitespace-pre-line'>{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
};
